import type { NextPage } from 'next';
import { NextSeo } from 'next-seo';
import Image from 'next/image';
import router from 'next/router';

import { isDesktop, isIOS } from 'react-device-detect';

import classNames from 'classnames';

import { APP_SCHEMA, ITUNES } from '@common/const';
import useOpenApp from '@common/deepLinking/useOpenApp';
import { isAndroidApp, isFlutterApp, isIOSApp } from '@common/webview';
import { HeaderV2Options } from '@type/web/header';

import usePortal from '@lib/hook/usePortal';
import { palette } from '@lib/styles/palette';

import Button from '@components/common/Button/Button';
import Icon from '@components/common/Icon';
import AppLayout from '@components/web/Layout/AppLayout';
import {
  memberDisputeWrapper,
  popupWrapper,
  appInducePopUpStyle,
  appInducePopUpTextStyle,
} from '@components/web/MemberDispute/MemberDisputeStyles';

const MemberDisputePage: NextPage = () => {
  /** 분쟁/불만 처리요청 전 필수사항 확인 팝업 */
  const {
    PopUp: QnaPopup,
    onShowPopUp: showQnaPopup,
    onHidePopUp: hideQnaPopup,
  } = usePortal('dim');
  /** 신고접수 전 필수사항 확인 팝업 */
  const {
    PopUp: ReportPopup,
    onShowPopUp: showReportPopup,
    onHidePopUp: hideReportPopup,
  } = usePortal('dim');
  /** 1:1 문의센터 앱 설치 유도 팝업 */
  const {
    PopUp: AppInducePopUp,
    onShowPopUp: activeAppInducePopUp,
    onHidePopUp: inactiveAppInducePopUp,
  } = usePortal('dim');

  /** 앱으로 가기 버튼 */
  const openApp = useOpenApp();
  const onClickOpenAppBtn = () => {
    let timer: NodeJS.Timeout;
    let goAppInterval: NodeJS.Timeout;
    const appstoreUrl = ITUNES;

    if (isIOS) {
      const clearTimer = () => {
        clearInterval(goAppInterval);
        clearTimeout(timer);
      };
      const isActiveBrowser = () => {
        // 앱 설치 되어있음
        if (document.hidden) {
          clearTimer();
        }
      };
      window.location.href = `${APP_SCHEMA}://?applink=qaseq&tab=on`;
      goAppInterval = setInterval(isActiveBrowser, 500);
      timer = setTimeout(() => {
        // 앱 미설치로 봄
        clearInterval(goAppInterval);
        if (window.confirm('앱스토어로 이동하시겠습니까?')) {
          window.location.href = appstoreUrl;
        }
      }, 2000);
    } else {
      openApp(`${APP_SCHEMA}://?applink=qaseq&tab=on`, true);
    }
  };

  const headerOptions: HeaderV2Options = {
    isFixed: {
      pc: false,
      tablet: true,
    },
    isTopPosition: false,
    isShowBackground: true,
    showAppDownloadBtn: false,
    showNavigationBar: [],
    title: {
      text: '회원 분쟁/불만 처리절차',
      showTitle: ['mobile', 'tablet', 'desktop'],
    },
  };
  return (
    <AppLayout headerOptions={headerOptions}>
      <NextSeo title="분쟁처리절차" />
      <main css={{ backgroundColor: '#e1e1e1' }}>
        <div css={memberDisputeWrapper}>
          <div className={'page pd_t56 memberDispute'}>
            <div>
              <div className="image-container">
                <Image
                  src={
                    'https://common.joongna.com/image/dispute/DisputeMain.png'
                  }
                  layout="fill"
                  quality={100}
                  objectFit="cover"
                  alt='회원 분쟁/불만 처리절차"'
                  className="image"
                />
              </div>
              {/* 처리절차 */}
              <div className={classNames('pd_t40 pd_h20', 'content')}>
                <h2 className="align_c pd_b30">
                  <img
                    src={
                      'https://common.joongna.com/image/dispute/DisputeTitle.png'
                    }
                    alt="회원 분쟁/불만 처리절차"
                    className={'title'}
                  />
                </h2>
                <div className={'processWrap'}>
                  <div className={classNames('column', 'top')}>
                    <div className={classNames('circle', 'dotLine')}>
                      <p className={'text'}>
                        <strong className="f15 c_orange">회원문의 접수</strong>
                      </p>
                    </div>
                    <div className={classNames('disc')}>
                      <div className={classNames('arrow', 'toRight')}>
                        <p className={'text'}>
                          <span className={'circleNum'}>1</span>
                          <span className="f12 c_orange">
                            분쟁/불만 처리요청
                          </span>
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-label="qna"
                        className={classNames('button', 'mt5')}
                        onClick={showQnaPopup}
                      >
                        <img
                          src={
                            'https://common.joongna.com/image/dispute/DisputeButton.png'
                          }
                          alt="접수 전 필수사항 확인"
                        />
                      </button>
                      <div className={classNames('arrow', 'toLeft', 'mt15')}>
                        <p className={'text'}>
                          <span className={'circleNum'}>3</span>
                          <span className="f12 c_orange">처리결과통지</span>
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label="1on1"
                      onClick={activeAppInducePopUp}
                      className={classNames('circle', 'solid')}
                    >
                      <p className={'text'}>
                        <strong className="f15 c_white vertical_m">
                          1:1문의센터
                        </strong>
                        <Icon icon="arrowRight" stroke="#fff" />
                      </p>
                    </button>
                  </div>
                  <div className={classNames('column', 'center')}>
                    <div className={classNames('disc')}>
                      <div className={classNames('arrow', 'toBottom')}>
                        <p className={'text'}>
                          <span className={'circleNum'}>4</span>
                          <span className="f12 c_orange">신고접수</span>
                        </p>
                        <button
                          type="button"
                          aria-label="report"
                          className={classNames('button', 'mt5')}
                          onClick={showReportPopup}
                        >
                          <img
                            src={
                              'https://common.joongna.com/image/dispute/DisputeButton.png'
                            }
                            alt="접수 전 필수사항 확인"
                          />
                        </button>
                      </div>
                    </div>
                    <div className={classNames('disc')}>
                      <div className={classNames('arrow', 'toBoth')}>
                        <p className={'text'}>
                          <span className={'circleNum'}>2</span>
                          <span className="f12 c_orange">사실확인</span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={classNames('column', 'bottom')}>
                    <a
                      href={
                        'https://cyberbureau.police.go.kr/crime/sub1.jsp?mid=010101'
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classNames('circle', 'solid')}
                    >
                      <p className={'text'}>
                        <strong className="f15 c_white vertical_m">
                          사이버수사국
                        </strong>
                        <Icon icon="arrowRight" stroke="#fff" />
                      </p>
                    </a>
                    <div className={classNames('disc')}>
                      <div className={classNames('arrow', 'toRight')}>
                        <p className={'text'}>
                          <span className={'circleNum'}>5</span>
                          <span className="f12 c_orange">
                            수사협조(서면접수)
                          </span>
                        </p>
                      </div>
                      <div className={classNames('arrow', 'toLeft', 'mt5')}>
                        <p className={'text'}>
                          <span className={'circleNum'}>6</span>
                          <span className="f12 c_orange">자료회신</span>
                        </p>
                      </div>
                    </div>
                    <div className={classNames('circle', 'line')}>
                      <p className={'text'}>
                        <strong className="f15 c_orange">소관부서</strong>
                      </p>
                    </div>
                  </div>
                </div>
                <div className={classNames('step', 'pd_t30')}>
                  <div className={'box'}>
                    <p className={classNames('f18', 'number')}>
                      <strong>1</strong>
                    </p>
                    <p className="f16">중고나라에 분쟁/불만 처리요청</p>
                  </div>
                  <div className={'box'}>
                    <p className={classNames('f18', 'number')}>
                      <strong>2</strong>
                    </p>
                    <p className="f16">중고나라 담당자 사실확인</p>
                  </div>
                  <div className={'box'}>
                    <p className={classNames('f18', 'number')}>
                      <strong>3</strong>
                    </p>
                    <p className="f16">최대 7영업일 이내 처리결과 통지</p>
                  </div>
                  <div className={'box'}>
                    <p className={classNames('f18', 'number')}>
                      <strong>4</strong>
                    </p>
                    <p className="f16">
                      사이버 수사국에 사기 신고접수
                      <br />
                      <span className="f14">(피해회원 직접신고)</span>
                    </p>
                  </div>
                  <div className={'box'}>
                    <p className={classNames('f18', 'number')}>
                      <strong>5</strong>
                    </p>
                    <p className="f16">
                      수사기관에서 중고나라에 수사협조요청
                      <br />
                      <span className="f14">(서면접수)</span>
                    </p>
                  </div>
                  <div className={'box'}>
                    <p className={classNames('f18', 'number')}>
                      <strong>6</strong>
                    </p>
                    <p className="f16">
                      수사기관에 자료 전달
                      <br />
                      <span className="f14">
                        (접수된 문서의 법적 요건 충족 된 자료만 회신)
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="pd_b30 pd_h20">
                <table className={classNames('mt30', 'termsTable')}>
                  <thead>
                    <tr>
                      <th className={'th_1'}>
                        <p className="bold f15">분쟁유형</p>
                      </th>
                      <th className={'th_2'}>
                        <p className="bold f15">처리기준 및 방법</p>
                      </th>
                      <th className={'th_3'}>
                        <p className="bold f15">비고</p>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="pd_l22">
                        <span>
                          <p className="bold">1.</p>
                        </span>
                        <p className="bold">일반등록상품</p>
                      </td>
                      <td />
                      <td />
                    </tr>
                    <tr className="c_orange">
                      <td className="pd_l22">
                        <span>
                          <p className="bold">-</p>
                        </span>
                        <p className="bold">신고/문의 접수 건</p>
                      </td>
                      <td>
                        <p>
                          신고/문의 접수 유형 중 운영정책에 의해 제재 사유가
                          확인되는 경우 이용제재 처리
                        </p>
                      </td>
                      <td />
                    </tr>
                    <tr>
                      <td className="pd_l22">
                        <span>
                          <p className="bold">-</p>
                        </span>
                        <p className="bold">취소할 경우</p>
                      </td>
                      <td>
                        <p className="bold">* 안전거래 이용시</p>

                        <p className="bold mt10">
                          [구매취소] 판매자의 배송준비 전
                        </p>
                        <p>
                          ▶구매내역 &gt; &ldquo;결제완료&rdquo; 거래 건 중
                          원하는 상품 선택 &gt; 거래취소
                        </p>

                        <p className="bold mt10">
                          [구매취소] 판매자의 배송준비 후
                        </p>
                        <p>
                          ▶구매내역 &gt; &ldquo;배송준비&rdquo; 거래 건 중 취소
                          희망하는 거래 선택 &gt; 거래취소요청
                        </p>

                        <p className="mt10">[판매취소]</p>
                        <p>
                          ▶판매내역 &gt; 취소 희망 하는 거래 &gt; 거래취소버튼
                          클릭 다만, 판매상품의 배송등록 전에만 취소할 수 있고
                          배송등록 후에는 취소가 불가
                        </p>
                        <p className="mt10">
                          * 안전거래 미 이용 시 직거래 경우 분쟁 당사자 끼리
                          합의 해야함.
                        </p>
                      </td>
                      <td>
                        <p className="f11">
                          거래취소를 요청 하면 판매자에게 취소요청이 전달 되며,
                          판매자의 요청 수락 시 취소
                        </p>
                        <p className="mt10 f11">
                          단, 판매자의 요청이 거절된 경우에는 거래 취소가
                          진행되지 않으며, 판매자가 거래취소 요청을 확인하지
                          않은 경우 72시간 후 자동거래 취소가 진행됩니다.
                        </p>
                        <p className="bold mt10 f11">
                          일반등록상품 구매취소 관련 분쟁 시 해당 책임은
                          판매자에게 있습니다.
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td className="pd_l22" rowSpan={2}>
                        <span>
                          <p className="bold">-</p>
                        </span>
                        <p className="bold">반품할 경우</p>
                      </td>
                      <td className="c_orange">
                        <p className="bold">* 안전거래 이용시</p>
                        <p className="mt10">
                          구매내역 &gt; &ldquo;배송완료&rdquo; 거래 건 중 원하는
                          상품 선택 &gt; 반품신청 버튼 클릭
                        </p>
                      </td>
                      <td className="c_orange" rowSpan={2}>
                        <p className="f11">
                          구매자 회원님이 반품 접수시 구매 확정과 판매대금
                          정산이 보류됩니다. 판/구매자 회원님은 충분한 협의 후
                          반송 진행 후 반송장 입력하여 환불 정산이 가능합니다.
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td className="c_orange">
                        <p className="bold">* 안전거래 미 이용시</p>

                        <p className="mt10">[구매취소] 판매자의 배송준비 전</p>
                        <p>직거래 경우 분쟁 당사자 끼리 합의 해야함.</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="align_c pd_t30">
                  {/* <Logo width={163} height={36} /> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* 분쟁/불만 처리요청 전 필수사항 확인 팝업 */}
      <QnaPopup>
        <div css={popupWrapper}>
          <p className={'popupHeader'}>1:1문의 접수 시 필수기재 사항</p>
          <div className={'popupBody'}>
            <div className={'popupContent'}>
              <p>신고접수시</p>
              <ul>
                <li>신고사유 입력 (상세하게 기재)</li>
                <li className={'necessary'}>상대방의 닉네임</li>
                <li className={'necessary'}>상대방의 상품사진</li>
                <li className={'necessary'}>외부거래링크 첨부</li>
                <li className={'necessary'}>상대방의 연락두절 정황</li>
                <li className={'necessary'}>앱 내 채팅내역과 입금내역</li>
                <li className={'necessary'}>그 외 사기 정황에 대한 증거자료</li>
              </ul>
            </div>
            <div className={classNames('popupContent', 'mt16')}>
              <p>불만접수시</p>
              <ul>
                <li>불만사유 입력 (상세하게 기재)</li>
                <li className={'optional'}>첨부파일</li>
              </ul>
            </div>
          </div>
          <button
            type="button"
            aria-label="hideQna"
            className={'popupFooter'}
            onClick={hideQnaPopup}
          >
            확인
          </button>
        </div>
      </QnaPopup>
      {/* ------------------------------ */}
      {/* 신고접수 전 필수사항 확인 팝업 */}
      <ReportPopup>
        <div css={popupWrapper}>
          <p className={'popupHeader'}>신고접수 시 필수기재 사항</p>
          <div className={'popupBody'}>
            <div className={'popupContent'}>
              <ul>
                <li className={'necessary'}>범행일시</li>
                <li>
                  이용한 사이트<span>중고나라</span>
                </li>
                <li>
                  사업자 등록번호<span>215-87-87482</span>
                </li>
                <li>범행에 이용한 아이디</li>
                <li>범행에 이용한 전화번호</li>
                <li>거래물품</li>
                <li className={'necessary'}>피해금액</li>
                <li className={'necessary'}>범행에 이용한 계좌번호</li>
                <li className={'necessary'}>은행명</li>
                <li className={'necessary'}>계좌주</li>
                <li className={'necessary'}>진술서</li>
              </ul>
            </div>
          </div>
          <button
            type="button"
            aria-label="hideReport"
            className={'popupFooter'}
            onClick={hideReportPopup}
          >
            확인
          </button>
        </div>
      </ReportPopup>
      {/* ------------------------------ */}
      {/* 1:1 문의센터 앱 설치 유도 팝업 */}
      <AppInducePopUp>
        <div css={appInducePopUpStyle}>
          <Icon
            className="close-btn"
            icon="close"
            size="14px"
            stroke={palette.black10}
            onClick={inactiveAppInducePopUp}
            css={{ cursor: 'pointer' }}
          />
          <div css={appInducePopUpTextStyle}>
            <div>중고나라</div>
            <div>해당 기능은 앱으로 이용 가능합니다.</div>
          </div>
          {!isDesktop && (
            <Button
              type="primary"
              fontSize="14px"
              fullWidth={true}
              onClick={onClickOpenAppBtn}
            >
              앱으로 가기
            </Button>
          )}
          <Button
            type={!isDesktop ? 'gray' : 'secondary'}
            fontSize="14px"
            fullWidth={true}
            onClick={() => {
              inactiveAppInducePopUp();
            }}
          >
            {!isDesktop ? '닫기' : '모바일 앱에서 이용해주세요'}
          </Button>
        </div>
      </AppInducePopUp>
      {/* ------------------------------ */}
    </AppLayout>
  );
};
export default MemberDisputePage;
